import { WsmErrorCode, WsmErrorFragment } from "@dashboard/graphql";
import commonErrorMessages, { getCommonFormFieldErrorMessage } from "@dashboard/utils/errors/common";
import { defineMessages, IntlShape } from "react-intl";

// The dealer members of WsmErrorCode. The backend sends its own merchant
// sentence in `message` and the Dashboard renders that verbatim; these strings
// are the fallback for a payload that carries a code and no sentence.
export const wsmDealerErrorMessages = defineMessages({
  customerAlreadyAssigned: {
    defaultMessage: "That customer is already in a dealer group", id: 'SDUz3e',
  },
  duplicateGroupCode: {
    defaultMessage: "That dealer group code is already in use", id: 'p7SU/L',
  },
  duplicateTierBreak: {
    defaultMessage: "This group already has a price for that SKU at that quantity", id: 'liUM4b',
  },
  groupInUse: {
    defaultMessage: "This dealer group still has customers in it", id: 'mUF9ts',
  },
  tierAmountBelowOneCent: {
    defaultMessage: "A dealer price has to be at least one cent", id: 'Do023v',
  },
  tierAmountTooManyDecimals: {
    defaultMessage: "A dealer price takes at most two decimal places", id: '9mRV+D',
  },
});

const codeToMessage = {
  [WsmErrorCode.CUSTOMER_ALREADY_ASSIGNED]: wsmDealerErrorMessages.customerAlreadyAssigned,
  [WsmErrorCode.DUPLICATE_GROUP_CODE]: wsmDealerErrorMessages.duplicateGroupCode,
  [WsmErrorCode.DUPLICATE_TIER_BREAK]: wsmDealerErrorMessages.duplicateTierBreak,
  [WsmErrorCode.GROUP_IN_USE]: wsmDealerErrorMessages.groupInUse,
  [WsmErrorCode.TIER_AMOUNT_BELOW_ONE_CENT]: wsmDealerErrorMessages.tierAmountBelowOneCent,
  [WsmErrorCode.TIER_AMOUNT_TOO_MANY_DECIMALS]:
    wsmDealerErrorMessages.tierAmountTooManyDecimals,
} as Partial<Record<WsmErrorCode, (typeof wsmDealerErrorMessages)["groupInUse"]>>;

/**
 * The model's own sentence first, this module's wording second, the Dashboard's
 * common wording last. Returns undefined for no error, so a helperText prop can
 * take the result directly.
 */
export const getWsmDealerErrorMessage = (
  error: WsmErrorFragment | undefined,
  intl: IntlShape,
): string | undefined => {
  if (!error) {
    return undefined;
  }

  if (error.message) {
    return error.message;
  }

  const message = error.code ? codeToMessage[error.code] : undefined;

  if (message) {
    return intl.formatMessage(message);
  }

  return getCommonFormFieldErrorMessage({ ...error, code: error.code }, intl);
};

export const wsmDealerUnknownErrorMessage = commonErrorMessages.unknownError;

export const getWsmDealerFieldError = (errors: WsmErrorFragment[], field: string) =>
  errors.find(error => error.field === field);

/**
 * The backend names a row inside a list input as `<list>.<index>.<field>`, which
 * for a 300-row paste is the only thing that says WHICH line was refused. This
 * turns that flat list into one error per row index so the grid can show it on
 * the row the merchant has to fix.
 */
export const getWsmIndexedErrors = (
  errors: WsmErrorFragment[],
  list: string,
): Record<number, WsmErrorFragment> =>
  errors.reduce<Record<number, WsmErrorFragment>>((acc, error) => {
    const match = error.field?.match(new RegExp(`^${list}\\.(\\d+)(\\.|$)`));

    if (match) {
      acc[Number(match[1])] = error;
    }

    return acc;
  }, {});

/** Errors with no `field`: the ones a page shows above its cards. */
export const getWsmDealerFormErrors = (errors: WsmErrorFragment[], intl: IntlShape): string[] =>
  errors
    .filter(error => !error.field)
    .map(error => getWsmDealerErrorMessage(error, intl) ?? intl.formatMessage(commonErrorMessages.unknownError));
